import { FC, Fragment } from 'react';

import { AnalyticEventAction } from 'analytics';
import { trackSectionUsageAnalyticsEvent } from 'analytics/events/section-usage';

import { observer } from 'mobx-react';

import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { sharedTestSelectors } from 'selectors/shared.selectors';

import { useStores } from 'mobx/hooks/useStores';

import { useWqOpenItemsSection } from 'hooks/useWqOpenItemsSection';

import { WorkQueueTabName } from 'views/WorkQueue/WorkQueue.types';

import { WorkQueueItemType, TicketSectionId } from 'views/WorkQueue/WorkQueue.types';

import WqCollapsibleSection from 'views/WorkQueue/WorkQueueOpenItemsView/WqCollapsibleSection';

import { WqSectionControl } from 'views/WorkQueue/WorkQueueOpenItemsView/WqSectionControl';

import { WqTicketPlaceholder } from 'views/WorkQueue/WqTicketPlaceholder';

import { CardRow } from 'components/Surfaces/Card';
import CallbackRequestTicketRow from 'components/Ticket/TicketRow/CallbackRequestTicketRow';
import OperatorTicketRow from 'components/Ticket/TicketRow/OperatorTicketRow';
import SymptomOperatorTicketRow from 'components/Ticket/TicketRow/SymptomOperatorTicketRow';
import TaskTicketRow from 'components/Ticket/TicketRow/TaskTicketRow';
import TicketReportRow from 'components/Ticket/TicketRow/TicketReportRow';

import { Section } from 'components/Ticket/TicketsContainers/TicketsContainers.constants';

const WqAssignedToMeSection: FC = () => {
  const { workQueueStore } = useStores();
  const { items, totalItems, totalPages, currentPage, isSectionPageLoading } =
    useWqOpenItemsSection(TicketSectionId.AssignedToMe);

  return (
    <div className="report-section highlight">
      <WqCollapsibleSection
        sectionName={Section.Assigned}
        totalItems={totalItems}
        onTrigger={(isOpen) =>
          trackSectionUsageAnalyticsEvent({
            action: isOpen ? AnalyticEventAction.Collapse : AnalyticEventAction.Expand,
            value: Section.Assigned,
            page_number: currentPage,
            tab: WorkQueueTabName.OpenItems
          })
        }
        testHook={sharedTestSelectors.ticketSection.container(TicketSectionId.AssignedToMe)}
      >
        {totalPages > 1 && <WqSectionControl sectionName={TicketSectionId.AssignedToMe} />}

        <div className="tickets-list">
          <TransitionGroup>
            {items.map(({ itemType, itemData }, index) => {
              const patient = workQueueStore.getPatientModel(itemData.patientId);

              return (
                <CSSTransition
                  key={itemData.id}
                  classNames="connect-ticket-transition"
                  timeout={600}
                >
                  <Fragment>
                    {isSectionPageLoading ? (
                      <WqTicketPlaceholder />
                    ) : (
                      <>
                        {itemType === WorkQueueItemType.OperatorTicket &&
                          !itemData.isSymptomOperatorTicket && (
                            <OperatorTicketRow
                              ticket={itemData}
                              withPatientLink
                              ticketIndex={index}
                              ticketSectionCurrentPage={currentPage - 1}
                              ticketSectionId={TicketSectionId.AssignedToMe}
                            />
                          )}

                        {itemType === WorkQueueItemType.OperatorTicket &&
                          itemData.isSymptomOperatorTicket && (
                            <SymptomOperatorTicketRow
                              ticket={itemData}
                              withPatientLink
                              ticketIndex={index}
                              ticketSectionCurrentPage={currentPage - 1}
                              ticketSectionId={TicketSectionId.AssignedToMe}
                            />
                          )}

                        {itemType === WorkQueueItemType.PatientTicket && (
                          <TicketReportRow
                            ticket={itemData}
                            reports={patient!.getTicketReportsSortedBySeverity(itemData.id)}
                            withPatientLink
                            ticketIndex={index}
                            ticketSectionCurrentPage={currentPage - 1}
                            ticketSectionId={TicketSectionId.AssignedToMe}
                          />
                        )}

                        {itemType === WorkQueueItemType.TaskTicket && (
                          <CardRow>
                            <TaskTicketRow
                              ticket={itemData}
                              withPatientLink
                              ticketIndex={index}
                              ticketSectionCurrentPage={currentPage - 1}
                              ticketSectionId={TicketSectionId.AssignedToMe}
                            />
                          </CardRow>
                        )}

                        {itemType === WorkQueueItemType.CallbackRequestTicket && (
                          <CallbackRequestTicketRow
                            ticket={itemData}
                            withPatientLink
                            ticketIndex={index}
                            ticketSectionCurrentPage={currentPage - 1}
                            ticketSectionId={TicketSectionId.AssignedToMe}
                          />
                        )}
                      </>
                    )}
                  </Fragment>
                </CSSTransition>
              );
            })}
          </TransitionGroup>
        </div>

        {totalPages > 1 && <WqSectionControl sectionName={TicketSectionId.AssignedToMe} isBottom />}
      </WqCollapsibleSection>
    </div>
  );
};

export default observer(WqAssignedToMeSection);
